import {useState} from 'react'
import * as Yup from 'yup'
import {Link, useNavigate} from 'react-router-dom'
import {FormikValues} from 'formik'
import {useDispatch} from 'react-redux'
import clsx from 'clsx'
import PhoneInput, {isValidPhoneNumber} from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import './Registration.css'
import {
  Api_V2_Auth_SendPasswordResetSms,
  Api_V2_Auth_VerifyPasswordResetSms,
} from '../../../apiClient/apiClient'
import {
  PasswordResetSmsApiData,
  PasswordResetSmsChallengeDTO,
  PasswordResetSmsSendRequest,
  PasswordResetSmsTokenDTO,
  PasswordResetSmsVerifyRequest,
} from '../../../types/PasswordResetSms'
import {setIsLoading, postRequestAsync} from '../../../redux/commonSlice'
import FormikForm from '../../../components/form/FormikForm'
import FormInput from '../../../components/form/FormInput'
import FormInputErrorMessage from '../../../components/common/messages/FormInputErrorMessage'
import FormErrorMessage from '../../../components/common/messages/FormErrorMessage'
import SubmitButton from '../../../components/form/button/SubmitButton'
import TranslatorText from '../../../components/common/translate/TranslatorText'
import Translator from '../../../components/common/translate/Translator'
import CountdownTimer from '../../../components/other/CountdownTimer'

const RESEND_THROTTLE_SECONDS = 60

const phoneSchema = Yup.object().shape({
  phoneNumber: Yup.string()
    .required('TELEFON_NUMARASI_REQUIRED')
    .test('is-valid-phone', 'TELEFON_NUMARASI_TELEFON', (value) =>
      value ? isValidPhoneNumber(value) : false
    ),
})

const codeSchema = Yup.object().shape({
  smsCode: Yup.string()
    .required('SMS_KODU_REQUIRED')
    .matches(/^\d{6}$/, 'Kod 6 haneli olmalıdır'),
})

export function ForgotPasswordSms() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [challenge, setChallenge] = useState<PasswordResetSmsChallengeDTO | null>(null)
  const [canResend, setCanResend] = useState(false)
  const [sendCount, setSendCount] = useState(0)

  const sendSms = async (phone: string): Promise<PasswordResetSmsApiData<PasswordResetSmsChallengeDTO>> => {
    const body: PasswordResetSmsSendRequest = {
      phoneNumber: phone.replaceAll(' ', ''),
    }

    return await dispatch<any>(
      postRequestAsync({
        postRequestFunction: Api_V2_Auth_SendPasswordResetSms,
        body: body,
      })
    ).unwrap()
  }

  const onPhoneSubmit = async (values: {phoneNumber: string}, actions: FormikValues) => {
    actions.setStatus(null)

    dispatch(setIsLoading(true))
    try {
      const response = await sendSms(values.phoneNumber)
      if (response?.data?.challengeGuid) {
        setPhoneNumber(values.phoneNumber)
        setChallenge(response.data)
        setCanResend(false)
        setSendCount((count) => count + 1)
      } else {
        actions.setStatus({
          isSuccess: false,
          message:
            (response?.serverMessage?.length ?? 0) > 0 ? response?.serverMessage : 'Bir Sorun Oluştu',
        })
      }
    } catch (error) {
      console.error(error)
      actions.setStatus({
        isSuccess: false,
        message: 'Bir Sorun Oluştu',
      })
    } finally {
      dispatch(setIsLoading(false))
    }
  }

  const onResend = async (setStatus: (status: any) => void) => {
    setStatus(null)

    dispatch(setIsLoading(true))
    try {
      const response = await sendSms(phoneNumber)
      if (response?.data?.challengeGuid) {
        setChallenge(response.data)
        setCanResend(false)
        setSendCount((count) => count + 1)
      } else {
        setStatus({
          isSuccess: false,
          message:
            (response?.serverMessage?.length ?? 0) > 0 ? response?.serverMessage : 'Bir Sorun Oluştu',
        })
      }
    } catch (error) {
      console.error(error)
      setStatus({
        isSuccess: false,
        message: 'Bir Sorun Oluştu',
      })
    } finally {
      dispatch(setIsLoading(false))
    }
  }

  const onCodeSubmit = async (values: {smsCode: string}, actions: FormikValues) => {
    actions.setStatus(null)

    dispatch(setIsLoading(true))
    try {
      const body: PasswordResetSmsVerifyRequest = {
        challengeGuid: challenge?.challengeGuid ?? '',
        code: values.smsCode,
      }

      const response: PasswordResetSmsApiData<PasswordResetSmsTokenDTO> = await dispatch<any>(
        postRequestAsync({
          postRequestFunction: Api_V2_Auth_VerifyPasswordResetSms,
          body: body,
        })
      ).unwrap()

      if ((response?.data?.resetToken?.length ?? 0) > 0) {
        navigate('/auth/new-password?code=' + encodeURIComponent(response.data!.resetToken!))
      } else {
        actions.setStatus({
          isSuccess: false,
          message:
            (response?.serverMessage?.length ?? 0) > 0 ? response?.serverMessage : 'Bir Sorun Oluştu',
        })
      }
    } catch (error) {
      console.error(error)
      actions.setStatus({
        isSuccess: false,
        message: 'Bir Sorun Oluştu',
      })
    } finally {
      dispatch(setIsLoading(false))
    }
  }

  if (challenge == null) {
    return (
      <FormikForm
        initialValues={{phoneNumber: phoneNumber}}
        validationSchema={phoneSchema}
        onSubmit={onPhoneSubmit}
      >
        {(props) => {
          return (
            <>
              <div className='text-center mb-10'>
                <div className='text-gray-500 fw-semibold fs-6'>
                  Telefon numaranıza şifre yenileme kodu gönderelim.
                </div>
              </div>
              <FormErrorMessage message={props.status?.message} />
              <div className='row mb-8'>
                <div className='d-flex flex-column'>
                  <PhoneInput
                    international
                    defaultCountry='TR'
                    className='country-select-metronic'
                    value={(props.values.phoneNumber as any) || undefined}
                    onChange={(value) => props.setFieldValue('phoneNumber', value || '')}
                    onBlur={() => props.setFieldTouched('phoneNumber', true)}
                    placeholder={TranslatorText('BOS_TELEFON')}
                    numberInputProps={{
                      className: clsx('form-control bg-transparent', {
                        'is-invalid': props.touched.phoneNumber && !!props.errors.phoneNumber,
                        'is-valid': props.touched.phoneNumber && !props.errors.phoneNumber,
                      }),
                    }}
                  />
                  <FormInputErrorMessage name='phoneNumber' />
                </div>
              </div>
              <div className='text-center'>
                <SubmitButton
                  className='w-100 mb-5'
                  isSubmitting={props.isSubmitting}
                  isValid={props.isValid}
                  loadingText='LUTFEN_BEKLEYIN'
                  notLoadingText='GONDER'
                />
                <Link to='/auth/login'>
                  <button type='button' className='btn btn-lg btn-light-danger w-100 mb-5'>
                    <Translator name='IPTAL' />
                  </button>
                </Link>
              </div>
            </>
          )
        }}
      </FormikForm>
    )
  }

  return (
    <FormikForm initialValues={{smsCode: ''}} validationSchema={codeSchema} onSubmit={onCodeSubmit}>
      {(props) => {
        return (
          <>
            <div className='text-center mb-10'>
              <div className='text-gray-500 fw-semibold fs-6'>
                {challenge.maskedPhone} numarasına gönderilen 6 haneli kodu girin.
              </div>
            </div>
            <FormErrorMessage message={props.status?.message} />
            <div className='row mb-8'>
              <div>
                <FormInput
                  istouched={props.touched.smsCode ?? false}
                  errorMessage={props.errors.smsCode}
                  isTransparent={true}
                  fieldAttributes={{
                    type: 'text',
                    name: 'smsCode',
                    autoComplete: 'one-time-code',
                    maxLength: 6,
                    inputMode: 'numeric',
                    placeholder: 'Kod',
                  }}
                />
              </div>
            </div>
            <div className='text-center'>
              <SubmitButton
                className='w-100 mb-5'
                isSubmitting={props.isSubmitting}
                isValid={props.isValid}
                loadingText='LUTFEN_BEKLEYIN'
                notLoadingText='GONDER'
              />
              <button
                type='button'
                className='btn btn-lg btn-light-primary w-100 mb-5'
                disabled={!canResend}
                onClick={() => {
                  if (canResend) {
                    onResend(props.setStatus)
                  }
                }}
              >
                <span>Tekrar Gönder </span>
                {!canResend && (
                  <CountdownTimer
                    key={sendCount}
                    seconds={RESEND_THROTTLE_SECONDS}
                    completedEvent={() => {
                      setCanResend(true)
                    }}
                  >
                    <></>
                  </CountdownTimer>
                )}
              </button>
              <button
                type='button'
                className='btn btn-lg btn-light-danger w-100 mb-5'
                onClick={() => {
                  setChallenge(null)
                  setCanResend(false)
                }}
              >
                Numarayı Değiştir
              </button>
            </div>
          </>
        )
      }}
    </FormikForm>
  )
}
